import { useId } from "react";
import { drillPerformance } from "./performance-model";
import { type Drill, dateLabel } from "./types";
import "./performance.css";

export function DrillPerformance({
  drill,
  className = "",
  headingLevel = 4,
}: {
  drill: Drill;
  className?: string;
  headingLevel?: 2 | 4;
}) {
  const id = useId();
  const Heading = headingLevel === 2 ? "h2" : "h4";
  const points = drillPerformance(drill.practices);
  const first = points[0];
  const latest = points.at(-1);
  const plot = points.map((point, index) => ({
    ...point,
    x: points.length === 1 ? 168 : 36 + (index / (points.length - 1)) * 264,
    y: 24 + (1 - point.rate / 100) * 120,
  }));
  return (
    <figure
      className={`drill-performance ${className}`}
      aria-labelledby={`${id}-heading`}
    >
      <figcaption>
        <Heading id={`${id}-heading`}>成功率の推移</Heading>
        {latest && (
          <p className="drill-performance-latest">
            <strong>
              {Math.round(latest.rate)}
              <small>%</small>
            </strong>
            <span>
              最新 · {latest.successes}/{latest.attempts}球
            </span>
          </p>
        )}
      </figcaption>
      {latest && first ? (
        <>
          <div className="drill-performance-plot">
            <div aria-hidden="true">
              {[0, 50, 100].map((value) => (
                <span
                  className="drill-performance-y-label"
                  key={value}
                  style={{
                    top: `${((24 + (1 - value / 100) * 120) / 160) * 100}%`,
                  }}
                >
                  {value}%
                </span>
              ))}
            </div>
            <svg
              className="drill-performance-chart"
              viewBox="0 0 320 160"
              role="img"
              aria-labelledby={`${id}-title ${id}-description`}
            >
              <title id={`${id}-title`}>{drill.title}の各練習の成功率</title>
              <desc id={`${id}-description`}>
                縦軸は0〜100%。古い練習から新しい練習の順に{points.length}
                件を表示。最新は
                {Math.round(latest.rate)}%、{latest.attempts}球中
                {latest.successes}球成功。
              </desc>
              {[0, 50, 100].map((value) => {
                const y = 24 + (1 - value / 100) * 120;
                return (
                  <g key={value} className="drill-performance-axis">
                    <line x1="36" x2="300" y1={y} y2={y} />
                  </g>
                );
              })}
              {plot.length > 1 && (
                <polyline
                  className="drill-performance-line"
                  points={plot
                    .map((point) => `${point.x},${point.y}`)
                    .join(" ")}
                />
              )}
              {plot.map((point) => (
                <circle
                  className="drill-performance-point"
                  key={point.id}
                  cx={point.x}
                  cy={point.y}
                  r={point.id === latest.id ? 4.5 : 3}
                >
                  <title>
                    {dateLabel(point.createdAt)} · {Math.round(point.rate)}%（
                    {point.successes}/{point.attempts}球）
                    {point.complete ? "" : " · 途中終了"}
                  </title>
                </circle>
              ))}
            </svg>
          </div>
          <div className="drill-performance-direction" aria-hidden="true">
            <span>古い</span>
            <span>新しい</span>
          </div>
          <div className="drill-performance-dates">
            <time dateTime={first.createdAt}>{dateLabel(first.createdAt)}</time>
            <time dateTime={latest.createdAt}>
              {dateLabel(latest.createdAt)}
            </time>
          </div>
          <details className="drill-performance-details">
            <summary>練習ごとの成績（{points.length}件）</summary>
            <ol>
              {points.map((point) => (
                <li key={point.id}>
                  <time dateTime={point.createdAt}>
                    {dateLabel(point.createdAt)}
                  </time>
                  <span>
                    {Math.round(point.rate)}%（{point.successes}/
                    {point.attempts}球）
                    {!point.complete && <small>途中終了</small>}
                  </span>
                </li>
              ))}
            </ol>
          </details>
        </>
      ) : (
        <div className="drill-performance-empty">
          <p>成功率を表示できる記録がありません。</p>
          <span>成功・ミスを記録するとグラフを表示します。</span>
        </div>
      )}
      <p className="drill-performance-note">
        直近20回の練習から、球数のある記録を表示。
        <br />
        成功率は成功球数 ÷ 記録した球数です。
      </p>
    </figure>
  );
}
